import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, User, Phone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import smartlyFavicon from "@/assets/smartly-favicon.png";
import smartlyLogo from "@/assets/smartly-main-logo.svg";

type Msg = { role: "user" | "assistant"; content: string };

interface ChatTrigger {
  id: string;
  trigger_type: string;
  name: string;
  config: Record<string, any>;
  enabled: boolean;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/website-chat`;

async function streamChat({
  messages,
  onDelta,
  onDone,
  onError,
}: {
  messages: Msg[];
  onDelta: (t: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (resp.status === 429) {
    onError("We're receiving a lot of questions right now. Please try again in a moment.");
    return;
  }
  if (resp.status === 402) {
    onError("The chat service is temporarily unavailable. Please try again later.");
    return;
  }
  if (!resp.ok || !resp.body) {
    onError("Something went wrong. Please try again.");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done = false;

  while (!done) {
    const { done: rd, value } = await reader.read();
    if (rd) break;
    buf += decoder.decode(value, { stream: true });

    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      let line = buf.slice(0, nl);
      buf = buf.slice(nl + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") { done = true; break; }
      try {
        const parsed = JSON.parse(json);
        const c = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (c) onDelta(c);
      } catch {
        buf = line + "\n" + buf;
        break;
      }
    }
  }

  // flush
  if (buf.trim()) {
    for (let raw of buf.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "" || !raw.startsWith("data: ")) continue;
      const json = raw.slice(6).trim();
      if (json === "[DONE]") continue;
      try {
        const p = JSON.parse(json);
        const c = p.choices?.[0]?.delta?.content as string | undefined;
        if (c) onDelta(c);
      } catch { /* ignore */ }
    }
  }

  onDone();
}

/* Simple markdown: bold, links, line breaks */
function renderMarkdown(text: string) {
  const parts: (string | JSX.Element)[] = [];
  const paragraphs = text.split(/\n{2,}/);
  
  paragraphs.forEach((para, pi) => {
    if (pi > 0) parts.push(<br key={`br-${pi}`} />);
    
    const lines = para.split("\n");
    lines.forEach((line, li) => {
      if (li > 0) parts.push(<br key={`ln-${pi}-${li}`} />);
      
      const regex = /(\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\))/g;
      let lastIdx = 0;
      let match;
      
      while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIdx) {
          parts.push(line.slice(lastIdx, match.index));
        }
        if (match[2]) {
          parts.push(<strong key={`b-${pi}-${li}-${match.index}`}>{match[2]}</strong>);
        } else if (match[3] && match[4]) {
          parts.push(
            <a
              key={`a-${pi}-${li}-${match.index}`}
              href={match[4]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1 mt-1 mb-1 rounded-md bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors no-underline"
            >
              {match[3]}
            </a>
          );
        }
        lastIdx = match.index + match[0].length;
      }
      if (lastIdx < line.length) {
        parts.push(line.slice(lastIdx));
      }
    });
  });

  return parts;
}

export default function AIChatWidget({ lifted = false }: { lifted?: boolean }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(typeof window !== "undefined" ? window.innerWidth < 1024 : false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobileOrTablet(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const isLeftSide = location.pathname === "/consultation" || isMobileOrTablet;

  /* ── CMS settings ── */
  const [chatEnabled, setChatEnabled] = useState<boolean | null>(() => {
    try {
      const v = localStorage.getItem("smartly_chat_enabled");
      return v === null ? null : v === "true";
    } catch { return null; }
  });
  const [livePersonEnabled, setLivePersonEnabled] = useState(true);
  const [livePersonPhone, setLivePersonPhone] = useState("18583335050");
  const [livePersonMessage, setLivePersonMessage] = useState("Hi, I'd like to speak with someone from Smartly Technologies.");
  const [welcomeMessage, setWelcomeMessage] = useState("Hi! I'm the Smartly assistant.\nAsk me anything about our IT services, cyber security, cloud solutions, and more.");
  const [assistantName, setAssistantName] = useState("Smartly Assistant");

  /* ── Triggers state ── */
  const [triggers, setTriggers] = useState<ChatTrigger[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const triggerShownRef = useRef<Set<string>>(new Set());
  const [proactiveMessage, setProactiveMessage] = useState<string | null>(null);

  useEffect(() => {
    // Load settings and triggers in parallel
    const loadSettings = supabase
      .from("site_content")
      .select("content_key, content_value")
      .in("content_key", [
        "chat.enabled", "chat.live_person_enabled", "chat.live_person_phone",
        "chat.live_person_message", "chat.welcome_message", "chat.assistant_name",
      ]);

    const loadTriggers = supabase
      .from("chatbot_triggers")
      .select("*")
      .eq("enabled", true)
      .order("sort_order", { ascending: true });

    Promise.all([loadSettings, loadTriggers]).then(([settingsRes, triggersRes]) => {
      const map: Record<string, string> = {};
      settingsRes.data?.forEach((r) => (map[r.content_key] = r.content_value));
      const enabled = map["chat.enabled"] !== "false";
      setChatEnabled(enabled);
      try { localStorage.setItem("smartly_chat_enabled", String(enabled)); } catch { /* ignore */ }
      setLivePersonEnabled(map["chat.live_person_enabled"] !== "false");
      if (map["chat.live_person_phone"]) setLivePersonPhone(map["chat.live_person_phone"]);
      if (map["chat.live_person_message"]) setLivePersonMessage(map["chat.live_person_message"]);
      if (map["chat.welcome_message"]) setWelcomeMessage(map["chat.welcome_message"]);
      if (map["chat.assistant_name"]) setAssistantName(map["chat.assistant_name"]);

      setTriggers((triggersRes.data as ChatTrigger[]) ?? []);
    });
  }, []);

  // ── Trigger handlers ──

  // Proactive message trigger
  useEffect(() => {
    if (!triggers.length || open) return;
    const proactiveTriggers = triggers.filter((t) => t.trigger_type === "proactive");

    const timers: NodeJS.Timeout[] = [];
    for (const trigger of proactiveTriggers) {
      const key = `proactive-${trigger.id}`;
      const shown = parseInt(sessionStorage.getItem(key) || "0");
      if (shown >= (trigger.config.max_shows || 1)) continue;

      const timer = setTimeout(() => {
        if (!triggerShownRef.current.has(key)) {
          triggerShownRef.current.add(key);
          sessionStorage.setItem(key, String(shown + 1));
          setProactiveMessage(trigger.config.message || "Hi there! Need help with anything?");
        }
      }, (trigger.config.delay_seconds || 10) * 1000);
      timers.push(timer);
    }

    return () => timers.forEach(clearTimeout);
  }, [triggers, open]);

  // Page-specific prompt trigger
  useEffect(() => {
    if (!triggers.length) return;
    const path = window.location.pathname;
    const pagePrompts = triggers.filter((t) => t.trigger_type === "page_prompt");

    for (const trigger of pagePrompts) {
      const triggerPath = trigger.config.page_path || "/";
      if (path === triggerPath || (triggerPath !== "/" && path.startsWith(triggerPath))) {
        if (trigger.config.message) {
          setWelcomeMessage(trigger.config.message);
        }
        if (trigger.config.suggested_questions) {
          const questions = trigger.config.suggested_questions.split("\n").filter(Boolean);
          setSuggestedQuestions(questions);
        }
        break;
      }
    }
  }, [triggers]);

  // Exit intent trigger (desktop only)
  useEffect(() => {
    if (!triggers.length || isMobile || open) return;
    const exitTriggers = triggers.filter((t) => t.trigger_type === "exit_intent");
    if (!exitTriggers.length) return;

    const handler = (e: MouseEvent) => {
      if (e.clientY <= 5) {
        for (const trigger of exitTriggers) {
          const key = `exit-${trigger.id}`;
          const shown = parseInt(sessionStorage.getItem(key) || "0");
          if (shown >= (trigger.config.max_shows || 1)) continue;

          sessionStorage.setItem(key, String(shown + 1));
          setProactiveMessage(trigger.config.message || "Before you go, can I help with anything?");
          break;
        }
      }
    };

    document.addEventListener("mouseout", handler);
    return () => document.removeEventListener("mouseout", handler);
  }, [triggers, isMobile, open]);

  // Scroll depth trigger
  useEffect(() => {
    if (!triggers.length || open) return;
    const scrollTriggers = triggers.filter((t) => t.trigger_type === "scroll_depth");
    if (!scrollTriggers.length) return;

    const handler = () => {
      const scrollPct = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      for (const trigger of scrollTriggers) {
        const key = `scroll-${trigger.id}`;
        const shown = parseInt(sessionStorage.getItem(key) || "0");
        if (shown >= (trigger.config.max_shows || 1)) continue;
        if (triggerShownRef.current.has(key)) continue;

        if (scrollPct >= (trigger.config.percentage || 50)) {
          triggerShownRef.current.add(key);
          sessionStorage.setItem(key, String(shown + 1));
          setProactiveMessage(trigger.config.message || "Interested in what you're reading?");
          break;
        }
      }
    };

    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [triggers, open]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Pin the mobile panel to the visual viewport (height + top offset)
  const [mobileBox, setMobileBox] = useState<{ top: number; height: number } | null>(null);

  useEffect(() => {
    if (open && inputRef.current && !isMobile) {
      inputRef.current.focus();
    }

    if (open && isMobile) {
      const scrollY = window.scrollY;
      const body = document.body;
      // Pause hidden page animations right away; lock the page after the slide finishes.
      document.documentElement.classList.add("chat-open");
      const lockTimer = window.setTimeout(() => {
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        document.documentElement.style.overflow = "hidden";
      }, 220);

      const preventScroll = (e: TouchEvent) => {
        const list = scrollRef.current;
        if (list && list.contains(e.target as Node)) return;
        if (e.target instanceof HTMLTextAreaElement) return;
        e.preventDefault();
      };
      document.addEventListener("touchmove", preventScroll, { passive: false });

      const vv = window.visualViewport;
      let raf = 0;
      const sync = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (vv) {
            setMobileBox({ top: Math.round(vv.offsetTop), height: Math.round(vv.height) });
          } else {
            setMobileBox({ top: 0, height: window.innerHeight });
          }
          setTimeout(scrollToBottom, 50);
        });
      };
      sync();
      vv?.addEventListener("resize", sync);
      vv?.addEventListener("scroll", sync);
      window.addEventListener("resize", sync);

      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(lockTimer);
        document.documentElement.classList.remove("chat-open");
        const wasLocked = body.style.position === "fixed";
        body.style.position = "";
        body.style.top = "";
        body.style.left = "";
        body.style.right = "";
        body.style.width = "";
        document.documentElement.style.overflow = "";
        if (wasLocked) window.scrollTo(0, scrollY);
        document.removeEventListener("touchmove", preventScroll);
        vv?.removeEventListener("resize", sync);
        vv?.removeEventListener("scroll", sync);
        window.removeEventListener("resize", sync);
        setMobileBox(null);
      };
    }
  }, [open, isMobile, scrollToBottom]);

  const send = async (text?: string) => {
    const msgText = (text || input).trim();
    if (!msgText || loading) return;
    setError(null);

    const userMsg: Msg = { role: "user", content: msgText };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSuggestedQuestions([]);
    setLoading(true);

    let assistantSoFar = "";
    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsert,
        onDone: () => setLoading(false),
        onError: (msg) => {
          setError(msg);
          setLoading(false);
        },
      });
    } catch {
      setError("Connection error. Please try again.");
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Don't render while loading settings, or if chat is disabled
  if (chatEnabled === null || chatEnabled === false) return null;

  const waUrl = `sms:${String(livePersonPhone).replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(livePersonMessage)}`;

  // Split welcome message for display
  const welcomeLines = welcomeMessage.split("\n");

  return (
    <>
      {/* Proactive message bubble */}
      <AnimatePresence>
        {proactiveMessage && !open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className={`fixed ${lifted ? 'bottom-40 sm:bottom-36' : 'bottom-20 sm:bottom-20'} ${isLeftSide ? 'left-4 sm:left-6' : 'right-4 sm:right-6'} z-50 max-w-xs`}
          >
            <div className={`bg-card border border-border rounded-2xl ${isLeftSide ? 'rounded-bl-md' : 'rounded-br-md'} shadow-xl px-4 py-3 text-sm text-foreground`}>
              <button
                onClick={() => setProactiveMessage(null)}
                className={`absolute -top-2 ${isLeftSide ? '-left-2' : '-right-2'} h-5 w-5 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground hover:text-foreground`}
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
              <p>{proactiveMessage}</p>
              <button
                onClick={() => { setOpen(true); setProactiveMessage(null); }}
                className="mt-2 text-xs font-medium text-primary hover:text-primary/80"
              >
                Chat now →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => { setOpen(true); setProactiveMessage(null); }}
            className={`fixed ${lifted ? 'bottom-32 sm:bottom-28' : 'bottom-4 sm:bottom-6'} ${isLeftSide ? 'left-4 sm:left-6' : 'right-4 sm:right-6'} z-50 flex items-center gap-2.5 rounded-full bg-green-600 text-white shadow-lg hover:bg-green-700 transition-colors pl-4 pr-3 py-3`}
            aria-label="Open chat assistant"
          >
            <span className="text-sm font-medium whitespace-nowrap">
              <span className="hidden sm:inline">Chat with Smartly</span>
              <span className="sm:hidden">Chat</span>
            </span>
            <MessageCircle className="h-5 w-5 flex-shrink-0" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={isMobile ? { y: "100%" } : { opacity: 0, y: 20, scale: 0.95 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={isMobile ? { y: "100%" } : { opacity: 0, y: 20, scale: 0.95 }}
            transition={isMobile ? { type: "tween", duration: 0.2, ease: "easeOut" } : { type: "spring", stiffness: 300, damping: 30 }}
            id="smartly-chat-panel"
            className={
              isMobile
                ? "fixed inset-x-0 top-0 z-50 flex flex-col bg-card overflow-hidden w-full"
                : `fixed bottom-6 ${isLeftSide ? 'left-6' : 'right-6'} z-50 flex flex-col w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100dvh-3rem)] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden`
            }
            style={isMobile ? {
              top: mobileBox ? mobileBox.top : 0,
              height: mobileBox ? mobileBox.height : '100dvh',
              maxHeight: mobileBox ? mobileBox.height : '100dvh',
              transform: 'translateZ(0)',
              overscrollBehavior: 'none',
            } : undefined}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/50" style={{ paddingTop: isMobile ? 'calc(0.75rem + env(safe-area-inset-top, 0px))' : undefined }}>
              <div className="flex items-center gap-2 min-w-0">
                <img src={smartlyLogo} alt="Smartly Technologies" className="h-6 w-auto object-contain" />
                <span className="text-muted-foreground text-xs font-medium border-l border-border pl-2">Assistant</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Close chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="relative flex-1 min-h-0 flex flex-col">
            <div ref={scrollRef} className="chat-scroll flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ overscrollBehaviorY: "contain" }}>
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-8 px-2">
                  <img src={smartlyFavicon} alt="Smartly" className="h-10 w-10 mx-auto mb-3 object-contain opacity-60" />
                  {welcomeLines.map((line, i) => (
                    <p key={i} className={i === 0 ? "font-medium text-foreground mb-1" : ""}>{line}</p>
                  ))}
                  {/* Suggested questions */}
                  {suggestedQuestions.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => send(q)}
                          className="block w-full text-left text-xs bg-secondary/80 hover:bg-secondary rounded-lg px-3 py-2 text-foreground transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {m.role === "assistant" && (
                    <div className="flex-shrink-0 mt-1">
                      <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                        <img src={smartlyFavicon} alt="Smartly" className="h-3.5 w-3.5 object-contain" />
                      </div>
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-secondary text-foreground rounded-bl-md"
                    }`}
                  >
                    {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                  </div>
                  {m.role === "user" && (
                    <div className="flex-shrink-0 mt-1">
                      <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex gap-2 justify-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <img src={smartlyFavicon} alt="Smartly" className="h-3.5 w-3.5 object-contain" />
                    </div>
                  </div>
                  <div className="bg-secondary rounded-2xl rounded-bl-md px-3.5 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              {error && (
                <div className="text-center text-destructive text-xs py-1">{error}</div>
              )}
            </div>
            </div>

            {/* Input */}
            <div className="border-t border-border p-3 space-y-2" style={{ paddingBottom: isMobile ? 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' : undefined }}>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setTimeout(scrollToBottom, 350);
                  }}
                  placeholder="Ask a question..."
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  style={{ maxHeight: 80, fontSize: 16 }}
                />
                <button
                  onClick={() => send()}
                  disabled={!input.trim() || loading}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              {livePersonEnabled && (
                <a
                  href={waUrl}
                  className="flex items-center justify-center gap-2 w-full rounded-xl border border-border bg-secondary/50 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Speak with a Live Person
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
